import { LocalStorageKeys } from '@/constants/enums/LocalStorageEnum';
import type { CommonResI } from '@/models/interface/response';
import { useUserStore } from '@/stores/user';
import axios from 'axios';
import dayjs from 'dayjs';

interface Bulletin {
  data: string;
  date: string;
  indx: string;
}

interface HiddenBulletinInfo {
  date: string;
  memberId: number;
}

function dto(content: Array<Bulletin>): any {
  const dates: Array<string> = [];
  const data: Array<{
    date: string;
    data: Array<string>;
  }> = [];
  content.forEach((v) => {
    const index = dates.indexOf(v.date);
    if (index == -1) {
      dates.push(v.date);
      data.push({
        date: v.date,
        data: [v.data]
      });
    }

    if (data[index]) {
      data[index]?.data?.push(v.data);
    }
  });
  return data;
}

export const useBulletin = () => {
  const appStore = useAppStore();
  const userStore = useUserStore();
  const { isBulletOpen } = storeToRefs(appStore);
  const today: string = dayjs().format('YYYY-MM-DD');

  const state = reactive<{
    isLoading: boolean;
    error: any; // todo: 之後定為數字。
    data: any;
  }>({
    isLoading: true,
    error: undefined,
    data: []
  });

  // 會員 ID (用於判斷是否同一會員在同一天隱藏佈告欄)
  const memberId = computed<number>(() => userStore.userData.memberId || 0);

  // 是否有佈告欄資料
  const hasBulletinList = computed<boolean>(() => state.data.length > 0);

  /**
   * 從 localStorage 取得佈告欄隱藏資訊
   */
  const getHiddenBulletinInfo = (): HiddenBulletinInfo | null => {
    const rawValue: string | null = localStorage.getItem(LocalStorageKeys.BulletinHideToday);
    if (!rawValue) return null;

    try {
      const parsed = JSON.parse(rawValue) as HiddenBulletinInfo;
      if (typeof parsed.date === 'string' && typeof parsed.memberId === 'number') {
        return parsed;
      }
    } catch {
      // 解析失敗，可能是資料格式不正確
      return {
        date: rawValue,
        memberId: 0
      };
    }

    return null;
  };

  /**
   * 是否顯示佈告欄
   */
  const shouldShow = (): boolean => {
    const hiddenInfo = getHiddenBulletinInfo();
    if (!hiddenInfo) return true;

    return hiddenInfo.date !== today && hiddenInfo.memberId === memberId.value;
  };

  /**
   * 今天不再顯示佈告欄
   */
  const hideToday = (): void => {
    const hiddenInfo: HiddenBulletinInfo = {
      date: today,
      memberId: memberId.value
    };

    localStorage.setItem(LocalStorageKeys.BulletinHideToday, JSON.stringify(hiddenInfo));
    isBulletOpen.value = false;
  };

  /**
   * 開啟佈告欄
   */
  const openBulletin = (): void => {
    if (!shouldShow() && !hasBulletinList.value) return;
    isBulletOpen.value = true;
  };

  /**
   * 關閉佈告欄
   */
  const closeBulletin = (): void => {
    isBulletOpen.value = false;
    hideToday();
  };

  /**
   * 切換佈告欄顯示狀態
   */
  const onTogglerBulletin = () => {
    isBulletOpen.value = !isBulletOpen.value;

    if (!isBulletOpen.value) {
      hideToday();
    }
  };

  async function fetchData() {
    axios
      .get(`https://funday.asia/api/bulletin.asp`)
      .then((res) => {
        const {
          errorCode,
          content
        }: CommonResI<{
          bulletin: Array<Bulletin>;
        }> = res?.data;
        if (res.status !== 200) {
          state.error = 500;
          return;
        }
        if (errorCode) {
          state.error = errorCode;
          return;
        }
        state.data = dto(content.bulletin);
      })
      .catch((err) => {
        state.error = err;
      })
      .finally(() => {
        state.isLoading = false;
      });
  }

  return {
    state,
    isBulletOpen,
    openBulletin,
    closeBulletin,
    onTogglerBulletin,
    hideToday,
    fetchData
  };
};
